// MARK://dhk8/pos/pages/TicketsPage
// Tickets (/tickets): el LISTADO escaneable de TODOS los tickets — recibos cerrados
// (PAGADO), pre-cuentas en curso (PRE-CUENTA · no fiscal) y cancelados (CANCELADO).
// Reconstruido con el MISMO lenguaje de tabla que el Admin (nui-table + barra de
// filtros): búsqueda, orden por columna, filtro de estatus, y — lo que pidió SAVIs —
// **rango de fechas** (presets + personalizado) para sacar periodos completos a la mano.
// El rango se resuelve en el SERVIDOR (listTickets from/to) para no limitarse a los
// últimos N. Cada fila abre /tickets/:id (ReceiptPage) — la vista de DETALLE no cambia.
//
// El patrón de barra-de-filtros + tabla vive ahora en el componente compartido
// `FilterableTable` (components/FilterableTable.tsx), reusable para futuros
// listados (p.ej. retiros de caja / petty-cash). Esta página solo aporta: las
// columnas, los accessors de búsqueda/estatus/orden, el rango server-side (vía
// `onDateRangeChange`) y las acciones específicas de pre-cuentas.
//
// Data: lib/clients/receipts.ts listTickets({from,to,limit}) — enriquecido con
// type/covers/itemCount/tipCents. El resumen (cobrado/propinas) se calcula sobre el
// conjunto filtrado (todas las páginas), contando SOLO los PAGADOS (no fabricar totales).

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Receipt } from 'lucide-react'
import { listTickets, type TicketStatus, type TicketSummary } from '../lib/clients/receipts.js'
import { cancelOrder } from '../lib/clients/orders.js'
import { requireManagerAuth } from '../features/security/pin-gate-store.js'
import { formatCents } from '../lib/money.js'
import { FilterableTable, StatusChip, type ChipTone, type FilterableColumn } from '../components/FilterableTable.js'

// ── helpers de fecha ──────────────────────────────────────────────────────────────
function fmtDateMx(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return new Intl.DateTimeFormat('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }).format(d)
}
function fmtTimeMx(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return new Intl.DateTimeFormat('es-MX', { hour: '2-digit', minute: '2-digit' }).format(d)
}

// ── enriquecido: tipo de orden ──────────────────────────────────────────────────
const TYPE_LABEL: Record<NonNullable<TicketSummary['type']>, string> = {
  DINE_IN: 'Comer aquí',
  TAKEOUT: 'Para llevar',
  DELIVERY: 'A domicilio',
  DRIVE_THROUGH: 'Auto',
}

const STATUS_TONE: Record<TicketStatus, ChipTone> = {
  PAGADO: 'success',
  'PRE-CUENTA': 'warning',
  CANCELADO: 'danger',
}

const STATUS_OPTIONS = [
  { value: 'ALL', label: 'Todos' },
  { value: 'PAGADO', label: 'Pagados' },
  { value: 'PRE-CUENTA', label: 'Pre-cuenta' },
  { value: 'CANCELADO', label: 'Cancelados' },
]

export default function TicketsPage() {
  const navigate = useNavigate()

  // Rango de fechas resuelto por FilterableTable (modo servidor): la página solo
  // guarda el resultado y lo usa como parte de la queryKey / params de listTickets.
  const [range, setRange] = useState<{ fromIso?: string; toIso?: string }>({})

  const ticketsQuery = useQuery({
    queryKey: ['tickets', 'list', range.fromIso ?? '', range.toIso ?? ''],
    queryFn: ({ signal }) =>
      listTickets(
        {
          ...(range.fromIso ? { from: range.fromIso } : {}),
          ...(range.toIso ? { to: range.toIso } : {}),
          limit: range.fromIso || range.toIso ? 500 : 100,
        },
        signal,
      ),
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  })
  const allTickets = ticketsQuery.data ?? []

  // Toast local (el POS no tiene toast global; cada página maneja el suyo).
  const [toast, setToast] = useState<{ msg: string; kind: 'ok' | 'err' } | null>(null)
  const flashToast = (msg: string, kind: 'ok' | 'err' = 'ok') => {
    setToast({ msg, kind })
    setTimeout(() => setToast(null), 2600)
  }

  // ── Acciones sobre pre-cuentas (órdenes abiertas) ──────────────────────
  // Cobrar → reanuda la orden en /order y abre el cobro directo (pay=1).
  // Reanudar → abre la comanda para seguir pidiendo (sin abrir cobro).
  // Cancelar → anula la orden con PIN de gerente + motivo obligatorio.
  const cobrar = (tk: TicketSummary) => navigate(`/order?orderId=${encodeURIComponent(tk.id)}&pay=1`)
  const reanudar = (tk: TicketSummary) => navigate(`/order?orderId=${encodeURIComponent(tk.id)}`)
  const cancelar = async (tk: TicketSummary) => {
    const auth = await requireManagerAuth('order.voidOrder', {
      requireReason: true,
      contextLabel: `Pre-cuenta ${tk.folio} — ${formatCents(tk.totalCents)}`,
    })
    if (!auth.proceed) return
    try {
      await cancelOrder(tk.id, (auth.reason ?? '').trim() || 'Anulada por gerente', auth.managerAuthToken)
      flashToast(`Pre-cuenta ${tk.folio} cancelada`)
      void ticketsQuery.refetch()
    } catch (e) {
      flashToast(e instanceof Error ? e.message : 'No se pudo cancelar la pre-cuenta', 'err')
    }
  }

  const columns: FilterableColumn<TicketSummary>[] = [
    {
      key: 'at',
      header: 'Fecha y hora',
      sortable: true,
      sortAccessor: (tk) => +new Date(tk.at),
      render: (tk) => (
        <>
          <div className="dhk8-tk-when__date">{fmtDateMx(tk.at)}</div>
          <div className="dhk8-tk-when__time">{fmtTimeMx(tk.at)}</div>
        </>
      ),
    },
    {
      key: 'folio',
      header: 'Folio',
      sortable: true,
      sortAccessor: (tk) => tk.folio,
      className: 'nui-tk-folio',
    },
    { key: 'tableLabel', header: 'Origen' },
    {
      key: 'type',
      header: 'Tipo',
      className: 'nui-tk-muted',
      render: (tk) => (tk.type ? TYPE_LABEL[tk.type] : '—'),
    },
    {
      key: 'covers',
      header: 'Comensales',
      align: 'right',
      className: 'nui-tk-num',
      render: (tk) => (tk.type === 'DINE_IN' && tk.covers ? tk.covers : '—'),
    },
    {
      key: 'totalCents',
      header: 'Total',
      sortable: true,
      sortAccessor: (tk) => tk.totalCents,
      align: 'right',
      className: 'nui-tk-num nui-tk-total',
      render: (tk) => formatCents(tk.totalCents),
    },
    {
      key: 'tipCents',
      header: 'Propina',
      align: 'right',
      className: 'nui-tk-num nui-tk-muted',
      render: (tk) => (tk.tipCents ? formatCents(tk.tipCents) : '—'),
    },
    {
      key: 'status',
      header: 'Estatus',
      render: (tk) => <StatusChip tone={STATUS_TONE[tk.status]}>{tk.status}</StatusChip>,
    },
  ]

  return (
    <div className="dhk8-tk-root">
      <header className="dhk8-tk-head">
        <div>
          <div className="dhk8-tk-eyebrow">Punto de venta · Tickets</div>
          <h1 className="dhk8-tk-title">Tickets</h1>
          <div className="dhk8-tk-sub">Cuentas emitidas, pre-cuentas y cancelados — filtra por fecha para sacar periodos</div>
        </div>
        <Receipt size={20} className="dhk8-tk-head-icon" aria-hidden />
      </header>

      <FilterableTable<TicketSummary>
        rows={allTickets}
        columns={columns}
        getRowKey={(tk) => `${tk.kind}:${tk.id}`}
        loading={ticketsQuery.isLoading}
        ariaLabel="Listado de tickets"
        searchable
        searchPlaceholder="Buscar folio, mesa o tipo…"
        searchAccessor={(tk) => `${tk.folio} ${tk.tableLabel} ${tk.type ? TYPE_LABEL[tk.type] : ''}`}
        statusOptions={STATUS_OPTIONS}
        statusAccessor={(tk) => tk.status}
        dateRangeEnabled
        onDateRangeChange={(fromIso, toIso) => setRange({ fromIso, toIso })}
        initialSort={{ key: 'at', dir: 'desc' }}
        pageSize={25}
        emptyState={allTickets.length === 0 ? 'No hay tickets en este periodo.' : 'Ningún ticket coincide con los filtros.'}
        renderSummary={(filtered) => {
          let cobrado = 0, propinas = 0, pagados = 0, pre = 0, cancelados = 0
          for (const tk of filtered) {
            if (tk.status === 'PAGADO') { pagados++; cobrado += tk.totalCents; propinas += tk.tipCents ?? 0 }
            else if (tk.status === 'PRE-CUENTA') pre++
            else cancelados++
          }
          return (
            <>
              <span><strong>{filtered.length.toLocaleString('es-MX')}</strong> tickets</span>
              <span className="nui-tk-summary__sep">·</span>
              <span><strong>{formatCents(cobrado)}</strong> cobrado <em>({pagados} pagados)</em></span>
              <span className="nui-tk-summary__sep">·</span>
              <span><strong>{formatCents(propinas)}</strong> en propinas</span>
              {pre > 0 && (<><span className="nui-tk-summary__sep">·</span><span>{pre} pre-cuenta</span></>)}
              {cancelados > 0 && (<><span className="nui-tk-summary__sep">·</span><span>{cancelados} cancelados</span></>)}
            </>
          )
        }}
        onRowClick={(tk) => navigate(`/tickets/${encodeURIComponent(tk.id)}`)}
        rowAriaLabel={(tk) => `Ticket ${tk.folio} · ${tk.tableLabel} · ${tk.status} · ${formatCents(tk.totalCents)}`}
        rowActions={(tk) =>
          tk.status === 'PRE-CUENTA' ? (
            <div className="nui-tk-actbar">
              <button type="button" className="nui-tk-act nui-tk-act--pay" onClick={() => cobrar(tk)}>Cobrar</button>
              <button type="button" className="nui-tk-act" onClick={() => reanudar(tk)}>Reanudar</button>
              <button type="button" className="nui-tk-act nui-tk-act--danger" onClick={() => void cancelar(tk)}>Cancelar</button>
            </div>
          ) : (
            <span className="nui-tk-muted">—</span>
          )
        }
      />

      {toast && <div className={`nui-tk-toast nui-tk-toast--${toast.kind}`} role="status">{toast.msg}</div>}

      <style>{styles}</style>
    </div>
  )
}

const styles = `
  .dhk8-tk-root { display:flex; flex-direction:column; gap:var(--pos-space-4,16px); min-height:0; flex:1; overflow-y:auto; -webkit-overflow-scrolling:touch; padding-bottom:var(--pos-space-4,16px); }
  .dhk8-tk-head { display:flex; justify-content:space-between; align-items:flex-start; gap:var(--pos-space-3,12px); }
  .dhk8-tk-head-icon { color:var(--pos-fg-tertiary,#7E8C82); flex-shrink:0; margin-top:2px; }
  .dhk8-tk-eyebrow { font-family:var(--font-mono,ui-monospace,monospace); font-size:var(--pos-text-xxs,11px); letter-spacing:.12em; text-transform:uppercase; color:var(--pos-fg-tertiary,#7E8C82); }
  .dhk8-tk-title { font-family:var(--font-display,system-ui); font-size:var(--pos-text-2xl,24px); font-weight:var(--pos-weight-semibold,600); color:var(--pos-fg,#E8EFE9); margin:var(--pos-space-1,4px) 0 0; }
  .dhk8-tk-sub { font-family:var(--font-mono,ui-monospace,monospace); font-size:var(--pos-text-xs,12px); color:var(--pos-fg-secondary,#B6BFB8); margin-top:2px; }

  /* ── Celdas específicas de Tickets ── */
  .nui-tk-when__date { color:var(--pos-fg,#E8EFE9); }
  .nui-tk-when__time { font-family:var(--font-mono,ui-monospace,monospace); font-size:var(--pos-text-xxs,11px); color:var(--pos-fg-tertiary,#7E8C82); font-variant-numeric:tabular-nums; margin-top:2px; }
  .nui-tk-folio { font-family:var(--font-mono,ui-monospace,monospace); font-weight:var(--pos-weight-semibold,600); font-variant-numeric:tabular-nums; }
  .nui-tk-muted { color:var(--pos-fg-secondary,#B6BFB8); }
  .nui-tk-num { font-family:var(--font-mono,ui-monospace,monospace); font-variant-numeric:tabular-nums; }
  .nui-tk-total { color:var(--pos-fg,#E8EFE9); font-weight:var(--pos-weight-medium,500); }

  /* ── Acciones por fila (pre-cuentas) ── */
  .nui-tk-actbar { display:inline-flex; gap:var(--pos-space-1,4px); justify-content:flex-end; flex-wrap:wrap; }
  .nui-tk-act { min-height:34px; padding:0 var(--pos-space-3,12px); border:1px solid var(--pos-separator-strong,rgba(255,255,255,.14)); border-radius:var(--pos-radius-md,8px); background:var(--pos-elevated,#1E2520); color:var(--pos-fg,#E8EFE9); font-family:var(--font-display,inherit); font-size:var(--pos-text-xs,12px); font-weight:var(--pos-weight-semibold,600); cursor:pointer; white-space:nowrap; transition:background var(--pos-duration-fast,120ms) var(--pos-ease-out,ease), border-color var(--pos-duration-fast,120ms) var(--pos-ease-out,ease); }
  .nui-tk-act:hover { background:var(--pos-surface,#161C18); }
  .nui-tk-act--pay { background:var(--pos-tint,#1F8B3A); border-color:var(--pos-tint,#1F8B3A); color:#fff; }
  .nui-tk-act--pay:hover { background:var(--pos-tint-hover,#1a7a33); }
  .nui-tk-act--danger { color:var(--pos-danger,#E5484D); border-color:color-mix(in oklch, var(--pos-danger,#E5484D) 40%, transparent); }
  .nui-tk-act--danger:hover { background:var(--pos-danger-bg,rgba(229,72,77,.14)); }

  /* ── Toast ── */
  .nui-tk-toast { position:fixed; left:50%; bottom:24px; transform:translateX(-50%); z-index:60; max-width:90vw; padding:var(--pos-space-3,12px) var(--pos-space-4,16px); border-radius:var(--pos-radius-lg,12px); font-family:var(--font-display,inherit); font-size:var(--pos-text-sm,13px); font-weight:var(--pos-weight-medium,500); box-shadow:0 8px 24px -8px rgba(0,0,0,.5); }
  .nui-tk-toast--ok { background:var(--pos-success,#1F8B3A); color:#fff; }
  .nui-tk-toast--err { background:var(--pos-danger,#E5484D); color:#fff; }
`
